import type { Body, Meta } from '@uppy/core'
import type { TusOptions } from '@uppy/tus'

import type { TusTransport } from '../values/UploadTransport'
import { renewCredentialsHooks } from './renewCredentials'

/**
 * Matches the part size the upload service's S3 store buffers, so one PATCH
 * maps to one part upstream.
 *
 * @see services/uploads/src/tusServer.ts
 */
export const CHUNK_SIZE_BYTES = 8 * 1024 * 1024

/** Same ceiling the aws-s3 transport puts on concurrent uploads. */
const CONCURRENT_UPLOAD_LIMIT = 10

/**
 * Metadata forwarded as `Upload-Metadata` — exactly what the upload service
 * reads, no more. `name` becomes the `filename` the service validates.
 *
 * @see services/uploads/src/uploadValidation.ts
 * @see services/uploads/src/uploadKey.ts
 */
const ALLOWED_META_FIELDS = ['name', 'organizationId']

export function createTusOptions(
  transport: TusTransport,
): TusOptions<Meta, Body> {
  return {
    endpoint: transport.endpoint,
    // The service authenticates the caller by the Buffer cookies already set
    // on *.buffer.com, which the browser withholds from a cross-origin request
    // unless it opts in.
    //
    // @see services/uploads/src/auth.ts
    withCredentials: true,
    limit: CONCURRENT_UPLOAD_LIMIT,
    chunkSize: CHUNK_SIZE_BYTES,
    allowedMetaFields: ALLOWED_META_FIELDS,
    // Sends the first chunk with the creation request (TUS
    // `creation-with-upload`), so a file that fits in one chunk is a single
    // request. That also spares the browser a preflight: the creation URL is
    // fixed and its preflight is cached, while every upload otherwise gets a
    // URL of its own that has to be preflighted before its first PATCH.
    uploadDataDuringCreation: true,
    // A finished upload is gone from the service, so a fingerprint left behind
    // would let a later file try to resume onto nothing.
    removeFingerprintOnSuccess: true,
    ...renewCredentialsHooks,
  }
}
